import api from './api';
import type {
    AdminDcaPlan,
    AdminDcaPlanPage,
    DcaBacktestRequest,
    DcaBacktestResult,
    DcaExecution,
    DcaMarketCandlesResult,
    DcaPlan,
    DcaPlanCost,
    DcaPlanCreateRequest,
    DcaPlanExecution,
    DcaPlanUpdateRequest,
    DcaUserConfig,
    DcaUserConfigUpdateRequest,
} from '../types/dca';

/** 研报对应 DCA 执行状态（含可执行性；未执行过时记录字段为空） */
export async function getDcaExecutionByReport(reportId: number): Promise<DcaExecution | null> {
    const res = await api.get(`/api/v1/dca/report/${reportId}/execution`);
    return res.data?.data ?? null;
}

/** 手动执行 DCA（研报页「执行 DCA」按钮；后端校验最新研报 + 时间窗口 + 每份仅一次） */
export async function executeDcaReport(reportId: number): Promise<DcaExecution | null> {
    const res = await api.post(`/api/v1/dca/report/${reportId}/execute`);
    return res.data?.data ?? null;
}

/* ==================== DCA 助手计划（/api/v1/dca/plans） ==================== */

/** 计划列表（当前用户，按创建时间倒序） */
export async function listDcaPlans(): Promise<DcaPlan[]> {
    const res = await api.get('/api/v1/dca/plans');
    return res.data?.data ?? [];
}

/** 创建计划（后端强校验条件/三层品种/会员上限/风险确认） */
export async function createDcaPlan(request: DcaPlanCreateRequest): Promise<DcaPlan | null> {
    const res = await api.post('/api/v1/dca/plans', request);
    return res.data?.data ?? null;
}

/** 计划详情 */
export async function getDcaPlan(planId: number): Promise<DcaPlan | null> {
    const res = await api.get(`/api/v1/dca/plans/${planId}`);
    return res.data?.data ?? null;
}

/** 编辑计划（仅 ACTIVE/PAUSED；编辑重置边沿信号） */
export async function updateDcaPlan(planId: number, request: DcaPlanUpdateRequest): Promise<DcaPlan | null> {
    const res = await api.put(`/api/v1/dca/plans/${planId}`, request);
    return res.data?.data ?? null;
}

/** 暂停计划（保留边沿状态） */
export async function pauseDcaPlan(planId: number): Promise<DcaPlan | null> {
    const res = await api.post(`/api/v1/dca/plans/${planId}/pause`);
    return res.data?.data ?? null;
}

/** 恢复计划（首次启用未确认风险时需带 riskConfirmed） */
export async function resumeDcaPlan(planId: number, riskConfirmed?: boolean): Promise<DcaPlan | null> {
    const res = await api.post(`/api/v1/dca/plans/${planId}/resume`, {riskConfirmed: !!riskConfirmed});
    return res.data?.data ?? null;
}

/** 停止计划（终态，不可恢复） */
export async function stopDcaPlan(planId: number): Promise<DcaPlan | null> {
    const res = await api.post(`/api/v1/dca/plans/${planId}/stop`);
    return res.data?.data ?? null;
}

/** 软删除计划 */
export async function deleteDcaPlan(planId: number): Promise<void> {
    await api.delete(`/api/v1/dca/plans/${planId}`);
}

/** 计划执行流水（按时间倒序） */
export async function listDcaPlanExecutions(planId: number): Promise<DcaPlanExecution[]> {
    const res = await api.get(`/api/v1/dca/plans/${planId}/executions`);
    return res.data?.data ?? [];
}

/** 计划成本追踪（P1：累计买入量/平均成本/现价/浮盈亏） */
export async function getDcaPlanCost(planId: number): Promise<DcaPlanCost | null> {
    const res = await api.get(`/api/v1/dca/plans/${planId}/cost`);
    return res.data?.data ?? null;
}

/** 计划测算（P2，F-19 条件回测；买入金额取用户 DCA 配置，回测不落计划/流水表） */
export async function backtestDcaPlan(request: DcaBacktestRequest): Promise<DcaBacktestResult | null> {
    const res = await api.post('/api/v1/dca/plans/backtest', request);
    return res.data?.data ?? null;
}

/** 计算器K线（无区间=最近 240 根；有区间=区间内降采样聚合；携带条件时返回指标序列） */
export async function getDcaMarketCandles(params: {
    symbol: string;
    timeframe: string;
    startMs?: number;
    endMs?: number;
    conditionsJson?: string;
}): Promise<DcaMarketCandlesResult> {
    const res = await api.get('/api/v1/dca/market/candles', {params});
    return res.data?.data ?? {candles: [], indicators: null};
}

/* ==================== 用户级 DCA 配置（/api/v1/dca/config） ==================== */

/** 用户 DCA 配置（含系统白名单选项） */
export async function getDcaUserConfig(): Promise<DcaUserConfig | null> {
    const res = await api.get('/api/v1/dca/config');
    return res.data?.data ?? null;
}

/** 更新用户 DCA 配置（品种收窄后端自动暂停受影响计划） */
export async function updateDcaUserConfig(request: DcaUserConfigUpdateRequest): Promise<DcaUserConfig | null> {
    const res = await api.put('/api/v1/dca/config', request);
    return res.data?.data ?? null;
}

/** 逗号分隔品种串 → 数组（去空） */
export const parseDcaSymbolList = (symbols?: string | null): string[] =>
    (symbols ?? '').split(',').map((s) => s.trim()).filter((s) => '' !== s);

/* ==================== 运营 DCA 管理（/api/v1/admin/dca，ADMIN） ==================== */

/** 全量计划分页列表（含归属用户；ADMIN） */
export async function adminListDcaPlans(page: number, size: number): Promise<AdminDcaPlanPage | null> {
    const res = await api.get('/api/v1/admin/dca/plans', {params: {page, size}});
    return res.data?.data ?? null;
}

/** 运营暂停计划（ADMIN，审计落库） */
export async function adminPauseDcaPlan(planId: number): Promise<AdminDcaPlan | null> {
    const res = await api.post(`/api/v1/admin/dca/plans/${planId}/pause`);
    return res.data?.data ?? null;
}

/** 运营停止计划（终态不可恢复；ADMIN，审计落库） */
export async function adminStopDcaPlan(planId: number): Promise<AdminDcaPlan | null> {
    const res = await api.post(`/api/v1/admin/dca/plans/${planId}/stop`);
    return res.data?.data ?? null;
}
